import { z } from "zod";
import { ClosedPeriod } from "./closed-periods.ts";
import { I18nText, I18nTextRequired, Id, LatLon, LOCALES, Slug, Timestamp } from "./common.ts";
import { ObjectType } from "./objects.ts";
import { Difficulty, PermanentClosure, SpotRatings } from "./spots.ts";

/**
 * Public, versioned spot export for other tools: GET /api/export/v1/spots.
 *
 * divespots.ch is the source of truth; a consumer keeps its own additions on top.
 * - Match by `id` (a stable UUID). Slugs and names can change; the id never does.
 * - Full sync: no `since` → every published spot, no tombstones.
 * - Incremental sync: `since=<generatedAt of the previous run>` → spots whose `updatedAt` is later
 *   than `since`, plus tombstones `{ id, removed: true, updatedAt }` for spots that were deleted
 *   or unpublished after `since`. A tombstone for an id the consumer never saw can be ignored.
 * - A spot's `updatedAt` here is the latest change of the spot OR any of its published objects.
 * - Only published spots and published objects; no diver accounts, sightings or suggestions.
 * - Additive changes (new optional fields) stay in v1; anything breaking gets /v2.
 */
export const EXPORT_VERSION = 1 as const;

export const SpotExportQuery = z.object({ since: Timestamp.optional() }).strict();
export type SpotExportQuery = z.infer<typeof SpotExportQuery>;

export const ExportObject = z
  .object({
    id: Id,
    type: ObjectType,
    name: I18nTextRequired,
    description: I18nText,
    location: LatLon,
    /** Resolved depth in metres (override ?? computed); null when unknown. */
    depthM: z.number().min(0).max(400).nullable(),
    depthSource: z.enum(["override", "computed"]).nullable(),
  })
  .strict();
export type ExportObject = z.infer<typeof ExportObject>;

const LocaleUrls = z.object({ de: z.url(), en: z.url(), fr: z.url(), it: z.url() }).strict();

export const ExportSpot = z
  .object({
    id: Id,
    removed: z.literal(false),
    slug: Slug,
    /** Spot page on divespots.ch (German). */
    url: z.url(),
    /** Spot page per UI language. */
    urls: LocaleUrls,
    name: z.string().min(1).max(120),
    /** Entry point. */
    location: LatLon,
    parkingLocation: LatLon.nullable(),
    lake: z.object({ slug: Slug, name: I18nTextRequired }).strict().nullable(),
    waterBody: z.string().max(120),
    locality: z.string().max(120),
    /** True when divespots.ch has a 3D lakebed view for this spot (`urls.*` + "/3d"). */
    has3d: z.boolean(),
    difficulty: Difficulty.nullable(),
    maxDepthM: z.number().min(0).max(400).nullable(),
    closedPeriods: z.array(ClosedPeriod),
    /** Closed for good; the consumer should hide or flag the spot. */
    permanentClosure: PermanentClosure.nullable(),
    description: I18nText,
    access: I18nText,
    parking: I18nText,
    entry: I18nText,
    hazards: I18nText,
    restrictions: I18nText,
    facilities: I18nText,
    ratings: SpotRatings,
    objects: z.array(ExportObject),
    createdAt: Timestamp,
    updatedAt: Timestamp,
  })
  .strict();
export type ExportSpot = z.infer<typeof ExportSpot>;

export const ExportTombstone = z
  .object({ id: Id, removed: z.literal(true), updatedAt: Timestamp })
  .strict();
export type ExportTombstone = z.infer<typeof ExportTombstone>;

export const SpotExport = z
  .object({
    version: z.literal(EXPORT_VERSION),
    /** Pass this as `since` on the next incremental run. */
    generatedAt: Timestamp,
    source: z.literal("https://divespots.ch"),
    /** Show this where the data is displayed. */
    attribution: z.string().min(1),
    spots: z.array(z.discriminatedUnion("removed", [ExportSpot, ExportTombstone])),
  })
  .strict();
export type SpotExport = z.infer<typeof SpotExport>;

/** Builds the per-language spot URLs; the API and tests share it. */
export function spotUrls(origin: string, slug: string): Record<(typeof LOCALES)[number], string> {
  return Object.fromEntries(LOCALES.map((l) => [l, `${origin}/${l}/spot/${slug}`])) as Record<
    (typeof LOCALES)[number],
    string
  >;
}
